<template>
  <div class="min-h-screen" v-if="entity">

    <PageHeader
      :title="pageHeaderTitle"
      :breadcrumbs="pageHeaderBreadcrumbs"
      background-image
      title-class="sensitive"
    >

      <template #actions>
        <div class="flex grow gap-2">
          <div>
            <DropdownMenu v-if="canEditEntityFields">
              <template #trigger>
                <FontAwesomeIcon :icon="['fas', 'ellipsis-vertical']" class="text-gray-600" />
              </template>
              <DropdownMenuItem @click="startEditingName">
                <FontAwesomeIcon :icon="['fas', 'edit']" class="mr-2 text-gray-400" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuItem v-if="canDeleteEntity" @click="showDeleteConfirm = true">
                <FontAwesomeIcon :icon="['fas', 'trash']" class="mr-2 text-red-400" />
                <span class="text-red-600">Delete Entity</span>
              </DropdownMenuItem>
            </DropdownMenu>
          </div>
          <div class="ml-auto flex gap-2">
            <router-link
              v-if="canAccessInsights"
              :to="`/insight/${entity.id}`"
              class="btn-secondary"
            >
              <FontAwesomeIcon :icon="['fas', 'chart-line']" class="mr-1" />
              View Insights
            </router-link>
          </div>
        </div>
      </template>

      <!-- Aligned metadata grid: every row shares one label column and one value column.
           Parents & Alerts stay mounted (v-show on a display:contents wrapper) so they can
           report their count and drop their whole row cleanly when empty. -->
      <dl class="mt-2 grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-2 items-baseline">
        <div class="contents" v-show="parentCount > 0">
          <dt class="text-sm font-medium text-gray-500">Parents:</dt>
          <dd class="min-w-0">
            <EntityParentNavigator :entityId="entity_id" :editable="canEditHierarchy" hide-label class="!mb-0" @count="parentCount = $event" />
          </dd>
        </div>

        <dt class="text-sm font-medium text-gray-500">IDs:</dt>
        <dd class="min-w-0">
          <EntityIdentifiers :entity-id="entity.id" hide-label />
        </dd>

        <template v-if="entity.linked_user">
          <dt class="text-sm font-medium text-gray-500">User:</dt>
          <dd class="min-w-0 text-sm sensitive">
            <router-link
              :to="`/tenantadmin/iam-console/users/${entity.linked_user.id}`"
              class="text-blue-600 hover:text-blue-800 hover:underline sensitive"
            >{{ entity.linked_user.full_name }}</router-link>
            <span class="text-gray-400 ml-1 sensitive">{{ entity.linked_user.email }}</span>
          </dd>
        </template>

        <template v-if="entityHasEngagementScores">
          <dt class="text-sm font-medium text-gray-500">Engagement:</dt>
          <dd class="min-w-0">
            <FormLogEngagementLevel :entity-id="entity.id" hide-label />
          </dd>
        </template>

        <dt class="text-sm font-medium text-gray-500">Tags:</dt>
        <dd class="min-w-0">
          <ListInfoTagRelationships :key="entity.id" :entity-id="entity.id" />
        </dd>

        <div class="contents" v-show="alertCount > 0">
          <dt class="text-sm font-medium text-gray-500">Alerts:</dt>
          <dd class="min-w-0">
            <AlertBadge :entity-id="entity.id" hide-label @count="alertCount = $event" />
          </dd>
        </div>
      </dl>

      <template #bottom v-if="availableTabs.length > 1">
        <Page_Tabs
          :initial-tab="selectedTab"
          :tabs="tabObjects"
          no-border
          @select="selectTab"
          class="px-6 mt-5"
        />
      </template>
    </PageHeader>

    <!-- Main Content Area with Lazy Loading -->
    <div class="px-20 py-6">
      <keep-alive :max="3">
        <div v-if="selectedTab" :key="selectedTab" class="space-y-6">
          <component
            v-for="componentConfig in getComponentsForTab(selectedTab)"
            :key="`${selectedTab}-${componentConfig.component}`"
            :is="getComponentType(componentConfig.component)"
            v-bind="getComponentProps(componentConfig)"
          />

        </div>
      </keep-alive>
    </div>
  </div>

  <!-- Loading State -->
  <LoadingIndicator v-else message="Loading entity details..." />

  <!-- Edit Name Modal -->
  <div
    v-if="showNameEditModal"
    class="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
    @click.self="cancelEditingName"
  >
    <div class="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
      <h3 class="text-lg font-semibold text-gray-900 mb-4">Edit Entity Name</h3>
      <input
        ref="nameInput"
        v-model="editingName"
        @keydown.enter="saveEntityName"
        @keydown.escape="cancelEditingName"
        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent"
      />
      <div class="flex justify-end gap-2 mt-4">
        <button
          @click="cancelEditingName"
          class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          @click="saveEntityName"
          :disabled="!editingName.trim() || editingName === entity?.name"
          class="btn-primary-sm"
        >
          Save
        </button>
      </div>
    </div>
  </div>

  <ConfirmationModal
    :isVisible="showNameChangeConfirm"
    :message="`Change entity name from '${entity?.name}' to '${editingName}'?`"
    @confirm="confirmNameChange"
    @cancel="showNameChangeConfirm = false"
  />

  <!-- Delete Confirmation Modal -->
  <ConfirmationModal
    :isVisible="showDeleteConfirm"
    :message="`Are you sure you want to delete '${entity?.name}' and ALL related objects (actions, threads, problems, etc.)? This action CANNOT be undone!`"
    @confirm="confirmDeleteEntity"
    @cancel="showDeleteConfirm = false"
  />

  <!-- Entity Creation Form -->
  <Form_Entity
    v-if="showEntityForm"
    :is-visible="showEntityForm"
    :entity-type-id="entityFormTypeId"
    :parent-entity-id="entity?.id"
    :parent-entity-name="entity?.name"
    @close="showEntityForm = false"
    @update="refreshCurrentTab"
  />

  <!-- Metric Configuration Modal -->
  <div
    v-if="showMetricConfigModal"
    class="fixed inset-0 z-50 overflow-y-auto"
  >
    <div class="flex items-center justify-center min-h-screen p-4">
      <div class="fixed inset-0 bg-gray-800/60" @click="showMetricConfigModal = false"></div>
      <div class="bg-white rounded-lg shadow-xl w-full max-w-lg relative z-10 flex flex-col" style="max-height: 80vh;">

        <!-- Header -->
        <div class="px-6 py-4 border-b border-gray-200">
          <h3 class="text-lg font-semibold text-gray-900">Configure Key Metrics</h3>
          <p class="text-sm text-gray-500 mt-1">Select which metrics to display on this entity's scorecard.</p>
        </div>

        <!-- Status banner: pending changes -->
        <div
          v-if="pendingEntityMetricIds.length > 0"
          class="px-6 py-3 border-b border-gray-200 bg-blue-50"
        >
          <div class="flex items-start gap-2">
            <FontAwesomeIcon :icon="['fas', 'info-circle']" class="text-blue-dark w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>
              <div class="text-sm text-blue-dark font-medium">
                {{ pendingEntityMetricIds.length }} metric{{ pendingEntityMetricIds.length !== 1 ? 's' : '' }} selected for this entity
                <span v-if="hasPendingChanges" class="text-amber-600 font-normal">&mdash; unsaved</span>
              </div>
              <div class="flex flex-wrap gap-1.5 mt-2">
                <span
                  v-for="metricId in pendingEntityMetricIds"
                  :key="'selected-' + metricId"
                  class="inline-flex items-center gap-1 bg-blue-100 text-blue-800 rounded-full px-3 py-1 text-xs font-medium"
                >
                  {{ composableScorecardMetricNames[metricId] || `Metric ${metricId}` }}
                  <button
                    @click="toggleMetric(metricId)"
                    class="ml-0.5 text-blue-600 hover:text-blue-900 rounded-full"
                  >
                    <FontAwesomeIcon :icon="['fas', 'times']" class="w-2.5 h-2.5" />
                  </button>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Status banner: using defaults (no pending selections) -->
        <div
          v-else
          class="px-6 py-3 border-b border-gray-200 bg-gray-50"
        >
          <div class="flex items-start gap-2">
            <FontAwesomeIcon :icon="['fas', 'info-circle']" class="text-gray-400 w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>
              <div class="text-sm text-gray-600">
                <template v-if="inheritedMetricSource === 'ancestor' && inheritedAncestorSource">
                  Inherited from <strong>{{ inheritedAncestorSource.entity_type_name }}: {{ inheritedAncestorSource.name }}</strong>
                  <span class="text-gray-400">&mdash; configured on a parent entity</span>
                </template>
                <template v-else-if="inheritedMetricSource === 'entity_type'">
                  Using <strong>{{ entityType?.type_name }}</strong> type defaults
                  <span class="text-gray-400">&mdash; configured in Entity Type admin</span>
                </template>
                <template v-else>
                  Using system defaults
                  <span class="text-gray-400">&mdash; primary metrics from performance domains</span>
                </template>
              </div>
              <div class="flex flex-wrap gap-1.5 mt-2">
                <span
                  v-for="metricId in inheritedMetricIds"
                  :key="'inherited-' + metricId"
                  class="inline-flex items-center bg-gray-100 text-gray-600 rounded-full px-3 py-1 text-xs"
                >
                  {{ composableScorecardMetricNames[metricId] || `Metric ${metricId}` }}
                </span>
              </div>
              <p class="text-xs text-gray-400 mt-2">Metrics without data for this entity won't appear on the scorecard. Select metrics below to override.</p>
            </div>
          </div>
        </div>

        <!-- Search -->
        <div class="px-6 pt-3">
          <input
            v-model="metricConfigSearch"
            type="text"
            placeholder="Search metrics..."
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent"
          />
        </div>

        <!-- Metric list -->
        <div class="px-6 py-3 overflow-y-auto flex-1">
          <div v-if="composableScorecardAllMetricIds.length === 0" class="text-sm text-gray-500 py-4 text-center">
            No metrics available
          </div>
          <div
            v-for="metricId in composableScorecardAllMetricIds.filter(id =>
              !metricConfigSearch || (composableScorecardMetricNames[id] || '').toLowerCase().includes(metricConfigSearch.toLowerCase())
            )"
            :key="metricId"
            :class="[
              'flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer transition-colors',
              isMetricSelected(metricId)
                ? 'bg-blue-50 border border-blue-200'
                : 'border border-transparent hover:bg-gray-50'
            ]"
            @click="toggleMetric(metricId)"
          >
            <input
              type="checkbox"
              :checked="isMetricSelected(metricId)"
              class="rounded border-gray-300 text-blue-dark focus:ring-blue-dark pointer-events-none"
            />
            <span :class="['text-sm', isMetricSelected(metricId) ? 'text-gray-900 font-medium' : 'text-gray-700']">
              {{ composableScorecardMetricNames[metricId] || `Metric ${metricId}` }}
            </span>
          </div>
        </div>

        <!-- Footer -->
        <div class="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <button
            v-if="pendingEntityMetricIds.length > 0"
            @click="clearEntityMetricOverride"
            class="btn-danger-sm"
          >
            <FontAwesomeIcon :icon="['fas', 'trash']" class="w-3 h-3" />
            Clear Override
          </button>
          <span v-else></span>
          <div class="flex gap-3">
            <button
              @click="showMetricConfigModal = false"
              class="btn-secondary-sm"
            >
              Cancel
            </button>
            <button
              @click="saveMetricConfig"
              :disabled="metricConfigSaving"
              class="btn-primary-sm"
            >
              {{ metricConfigSaving ? 'Saving...' : 'Save' }}
            </button>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { h, defineAsyncComponent } from 'vue';
import axios from 'axios';
import { mapGetters } from 'vuex';
import { toInteger } from 'lodash';
import Form_Manage_Entity_Parents from "@/components/Entity/Form_Manage_Entity_Parents.vue";
import ConfirmationModal from '@/components/widgetcomponents/ConfirmationModal.vue';

// Lazy load heavy components
const Action_Table = defineAsyncComponent(() => import('@/components/Lists/Action_Table.vue'));
const Engagement_Table = defineAsyncComponent(() => import('@/components/Lists/Engagement_Table.vue'));
const Entity_Detail_Table = defineAsyncComponent(() => import('@/components/Entity/Entity_Detail_Table.vue'));
const Survey_Table = defineAsyncComponent(() => import('@/components/Lists/Survey_Table.vue'));
const Contract_Attribution_Table = defineAsyncComponent(() => import('@/components/Lists/Contract_Attribution_Table.vue'));
const Practice_Metric_Scorecard_Detailed = defineAsyncComponent(() => import('@/components/Metric/Metric_Tables/Practice_Metric_Scorecard_Detailed.vue'));
const Composable_Metric_Scorecard = defineAsyncComponent(() => import('@/components/InsightsNew/ComposableMetricScorecard.vue'));
const Quality_Scorecard = defineAsyncComponent(() => import('@/components/Quality/Quality_Scorecard.vue'));
const CareGapSummary = defineAsyncComponent(() => import('@/components/Quality/CareGapSummary.vue'));
const PatientWorklist = defineAsyncComponent(() => import('@/components/InsightsNew/PatientWorklist.vue'));
const PPIModule = defineAsyncComponent(() => import('@/components/Entity/PPIModule.vue'));
const MetricInsightsWidget = defineAsyncComponent(() => import('@/components/Metric/MetricInsightsWidget.vue'));
const ADT_Episodes_List = defineAsyncComponent(() => import('@/components/Metric/Metric_Insights/51/51_1_ADT_EPISODES_LIST.vue'));
const TOCSankeyInsight = defineAsyncComponent(() => import('@/components/Metric/Metric_Insights/51/51_3_TOC_SANKEY.vue'));
const Details_EntityContext = defineAsyncComponent(() => import('@/components/Entity/Details_EntityContext.vue'));
const Entity_Table = defineAsyncComponent(() => import('@/components/Entity/Entity_Table.vue'));

const Attribution_Health = defineAsyncComponent(() => import('@/components/Entity/Attribution_Health.vue'));
const StrategicAssessments = defineAsyncComponent(() => import('@/components/Entity/StrategicAssessments.vue'));
const EntityNotes = defineAsyncComponent(() => import('@/components/Entity/EntityNotes.vue'));
const EntityIntelligencePanel = defineAsyncComponent(() => import('@/components/PersonalAnalyst/EntityIntelligencePanel.vue'));
const EngagementTrackerPanel = defineAsyncComponent(() => import('@/components/EngagementTracker/EngagementTrackerPanel.vue'));
const EntityReports = defineAsyncComponent(() => import('@/components/PersonalAnalyst/EntityReports.vue'));
const ActuarialBenchmarks = defineAsyncComponent(() => import('@/components/Growth/ActuarialBenchmarks.vue'));

// Import lightweight components normally
import Generic_Form from '@/components/forms/Generic_Form.vue';
import Form_Entity from '@/components/forms/Form_Entity.vue';
import Create_Engagement_Button from '@/components/widgetcomponents/Create_Engagement_Button.vue';
import Page_Tabs from '@/components/widgetcomponents/Page_Tabs.vue';
import AttributeField from "@/components/Entity/AttributeField.vue";
import ListInfoTagRelationships from "@/components/Lists/ListInfoTagRelationships.vue";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faSpinner, faPlus, faEdit, faSave, faTrash, faCheck, faTimes,
  faChevronDown, faChevronUp, faExternalLinkAlt, faInfoCircle,
  faWandMagicSparkles, faGear, faChartLine
} from '@fortawesome/free-solid-svg-icons';
import ComposableAlertTable from "@/components/InsightsNew/ComposableAlertTable.vue";
import EntityParentNavigator from "@/components/Entity/EntityParentNavigator.vue";
import EntityIdentifiers from "@/components/Entity/EntityIdentifiers.vue";
import AlertBadge from "@/components/widgetcomponents/AlertBadge.vue";
import spiralDots from '@/assets/spiral_dots.svg';
import Toggle from '../widgetcomponents/Toggle.vue';
import FormLogEngagementLevel from "@/components/Details/Detail_EntityEngagementLevel.vue";
import { FEATURES } from '@/constants/features';
import { canAccessFeature } from '@/utils/access';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import PageHeader from '../widgetcomponents/library/PageHeader.vue';
import ComponentHeader from '../widgetcomponents/library/ComponentHeader.vue';
import DropdownMenu from '../widgetcomponents/ui/DropdownMenu.vue';
import DropdownMenuItem from '../widgetcomponents/ui/DropdownMenuItem.vue';
import { calendarDate } from '@/utils/common';

library.add(
  faSpinner, faPlus, faEdit, faSave, faTrash, faCheck, faTimes,
  faChevronDown, faChevronUp, faExternalLinkAlt, faInfoCircle,
  faWandMagicSparkles, faGear, faChartLine
);

export default {
  name: 'Overview_Entity',
  components: {
    EntityIdentifiers,
    FontAwesomeIcon,
    ConfirmationModal,
    ListInfoTagRelationships,
    Generic_Form,
    Form_Entity,
    Create_Engagement_Button,
    Page_Tabs,
    AttributeField,
    Action_Table,
    Engagement_Table,
    Entity_Detail_Table,
    Survey_Table,
    Contract_Attribution_Table,
    Practice_Metric_Scorecard_Detailed,
    Composable_Metric_Scorecard,
    MetricInsightsWidget,
    ADT_Episodes_List,
    TOCSankeyInsight,
    Form_Manage_Entity_Parents,
    EntityParentNavigator,
    Entity_Table,
    AlertBadge,
    FormLogEngagementLevel,
    PageHeader,
    ComponentHeader,
    DropdownMenu,
    DropdownMenuItem,
    LoadingIndicator,
    EntityNotes
  },
  props: {
    entity_id: {
      type: [String, Number],
      required: true
    },
    tab: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      entity: null,
      parentCount: 0,
      alertCount: 0,
      entityType: null,
      entityAttributes: [],
      entityData: {},
      selectedTab: 'Overview',
      refreshKey: 0,
      observationYear: 2026,
      backgroundImage: spiralDots,
      scorecard_observation_year: 2026,
      list_observation_year: 2026,
      composableScorecardAllMetricIds: [],
      composableScorecardKeyMetricIds: [],
      composableScorecardMetricNames: {},
      composablePivotMode: false,
      showActiveTCM: true,
      adtEpisodeStart: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().slice(0, 10),
      adtEpisodeEnd: new Date().toISOString().slice(0, 10),
      loadedTabs: new Set(['Overview']),
      tabRefreshKeys: {},
      showNameEditModal: false,
      editingName: '',
      showNameChangeConfirm: false,
      showDeleteConfirm: false,
      showCompleted: false,
      showEntityForm: false,
      entityFormTypeId: null,
      entityTypesWithEngagementScores: ['Practice', 'Staff', 'Location'],
      showMetricConfigModal: false,
      metricConfigSearch: '',
      metricConfigSaving: false,
      pendingEntityMetricIds: [],
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    ...mapGetters('tenant', ['isTenantAdmin', 'current_tenant_name', 'current_tenant_slug']),

    pageHeaderTitle() {
      return this.entity ? this.entity.name : 'Entity Overview';
    },

    pageHeaderBreadcrumbs() {
      if (!this.entity) return [];
      return [
        { label: this.entityType?.type_name || 'Entity' },
      ];
    },

    canEditEntityFields() {
      const userRoles = this.current_user?.tenant_access?.[this.current_tenant_slug]?.roles || [];
      
      // Temporary logic for tenant RBAC
      if (this.current_tenant_name?.toLowerCase() === 'vytalize') {
        return userRoles.some(role => ['security', 'admin', 'standard', 'manager_viewer'].includes(role));
      }
      
      return userRoles.includes('admin');
    },

    availableTabs() {
      const layoutConfig = this.entityType?.display_config?.layout_config;
      if (!layoutConfig?.tabs) return ['Overview'];
      const allTabNames = Object.keys(layoutConfig.tabs);
      if (allTabNames.length === 0) return ['Overview'];
      const tabOrder = layoutConfig.tab_order || [];
      const ordered = tabOrder.filter(name => allTabNames.includes(name));
      const remaining = allTabNames.filter(name => !ordered.includes(name));
      const tabs = [...ordered, ...remaining];
      // Drop tabs whose every component is hidden for this user (e.g. superuser-only
      // tabs for non-superusers) so they don't render as empty tabs in the nav.
      const visible = tabs.filter(name => {
        const comps = layoutConfig.tabs[name] || [];
        if (!comps.length) return true;
        return comps.some(c => !this.componentHidden(c.component));
      });
      return visible.length ? visible : ['Overview'];
    },

    tabObjects() {
      return this.availableTabs.map(tab => ({
        label: tab,
        id: tab,
        to: `/entity/${this.entity_id}/${tab}`
      }));
    },

    canEditHierarchy() {
      return canAccessFeature(FEATURES.ACTION_HIERARCHY_CONFIG);
    },

    canDeleteEntity() {
      return canAccessFeature(FEATURES.ACTION_ENTITY_DELETE);
    },

    canAccessInsights() {
      return canAccessFeature(FEATURES.MODULE_INSIGHTS);
    },

    entityHasEngagementScores() {
      return this.entityTypesWithEngagementScores.includes(this.entityType?.type_name);
    },

    adtEpisodesSqlFilters() {
      const filters = {};
      const hasStart = !!this.adtEpisodeStart;
      const hasEnd = !!this.adtEpisodeEnd;

      if (hasStart && hasEnd) {
        filters['episode_start'] = {
          'BETWEEN': [
            this.dateToIsoStart(this.adtEpisodeStart),
            this.dateToIsoEnd(this.adtEpisodeEnd)
          ]
        };
      } else if (hasStart) {
        filters['episode_start'] = { '>=': this.dateToIsoStart(this.adtEpisodeStart) };
      } else if (hasEnd) {
        filters['episode_start'] = { '<': this.dateToIsoEnd(this.adtEpisodeEnd) };
      }

      return filters;
    },

    adtEpisodesKey() {
      return `${this.showActiveTCM}|${this.adtEpisodeStart || ''}|${this.adtEpisodeEnd || ''}`;
    },

    currentTabRefreshKey() {
      return this.tabRefreshKeys[this.selectedTab] || 0;
    },

    hasPendingChanges() {
      const saved = this.entity?.display_config?.scorecard_metric_ids || [];
      const pending = this.pendingEntityMetricIds;
      if (saved.length !== pending.length) return true;
      return !saved.every((id, i) => pending[i] === id);
    },

    entityTypeMetricSettings() {
      // Find the metric component settings from the entity type's display_config
      const tabs = this.entityType?.display_config?.layout_config?.tabs || {};
      for (const tabComponents of Object.values(tabs)) {
        for (const comp of tabComponents) {
          if (comp.component === 'composable_key_metrics') {
            if (comp.settings?.metricIds_by_year || comp.settings?.metricIds) {
              return comp.settings;
            }
          }
        }
      }
      return null;
    },

    inheritedMetricIds() {
      // Return the metric IDs that would be used if no entity-level override exists
      const inherited = this.entity?.inherited_scorecard_config;
      if (inherited?.scorecard_metric_ids_by_year?.[this.scorecard_observation_year]?.length) {
        return inherited.scorecard_metric_ids_by_year[this.scorecard_observation_year];
      }
      if (inherited?.scorecard_metric_ids?.length) {
        return inherited.scorecard_metric_ids;
      }
      const settings = this.entityTypeMetricSettings;
      if (settings?.metricIds_by_year?.[this.scorecard_observation_year]?.length) {
        return settings.metricIds_by_year[this.scorecard_observation_year];
      }
      if (settings?.metricIds?.length) {
        return settings.metricIds;
      }
      return this.composableScorecardKeyMetricIds;
    },

    inheritedMetricSource() {
      const inherited = this.entity?.inherited_scorecard_config;
      if (inherited?.scorecard_metric_ids_by_year?.[this.scorecard_observation_year]?.length
          || inherited?.scorecard_metric_ids?.length) {
        return 'ancestor';
      }
      return this.entityTypeMetricSettings ? 'entity_type' : 'system';
    },

    inheritedAncestorSource() {
      return this.entity?.inherited_scorecard_config?.source || null;
    },
  },
  mounted() {
    this.fetchEntityDetails();
    this.fetchAllComposableMetricIds();
  },
  watch: {
    entity_id(newVal) {
      if (newVal) {
        this.loadedTabs.clear();
        this.tabRefreshKeys = {};
        this.fetchEntityDetails();
        this.fetchAllComposableMetricIds();
      }
    },
    tab(newTab) {
      if (!newTab) return
      const match = this.resolveTab(newTab)
      if (match && this.selectedTab !== match) {
        this.selectedTab = match
        localStorage.setItem(`entity_${this.entity_id}_tab`, match)
      }
    },
    selectedTab(newTab) {
      this.loadedTabs.add(newTab);
      if (!(newTab in this.tabRefreshKeys)) {
        this.tabRefreshKeys[newTab] = 0;
      }
    }
  },
  methods: {
    toInteger,

    getResolvedMetricIds(componentInstance) {
      // Tier 1: Entity-level override
      const entityConfig = this.entity?.display_config;
      if (entityConfig?.scorecard_metric_ids_by_year?.[this.scorecard_observation_year]?.length) {
        return entityConfig.scorecard_metric_ids_by_year[this.scorecard_observation_year];
      }
      if (entityConfig?.scorecard_metric_ids?.length) {
        return entityConfig.scorecard_metric_ids;
      }
      // Tier 1.5: Inherited from nearest ancestor (resolved server-side via closure walk)
      const inherited = this.entity?.inherited_scorecard_config;
      if (inherited?.scorecard_metric_ids_by_year?.[this.scorecard_observation_year]?.length) {
        return inherited.scorecard_metric_ids_by_year[this.scorecard_observation_year];
      }
      if (inherited?.scorecard_metric_ids?.length) {
        return inherited.scorecard_metric_ids;
      }
      // Tier 2: Entity-type component settings (year-specific)
      const settings = componentInstance?.componentConfig?.settings;
      if (settings?.metricIds_by_year?.[this.scorecard_observation_year]?.length) {
        return settings.metricIds_by_year[this.scorecard_observation_year];
      }
      // Tier 3: Entity-type component settings (default)
      if (settings?.metricIds?.length) {
        return settings.metricIds;
      }
      // Tier 4: System default (primary domain metrics)
      return this.composableScorecardKeyMetricIds;
    },

    getMetricConfigSource() {
      const entityConfig = this.entity?.display_config;
      if (entityConfig?.scorecard_metric_ids_by_year?.[this.scorecard_observation_year]?.length
          || entityConfig?.scorecard_metric_ids?.length) {
        return 'entity';
      }
      return this.inheritedMetricSource;
    },

    openMetricConfig() {
      const entityIds = this.entity?.display_config?.scorecard_metric_ids || [];
      this.pendingEntityMetricIds = [...entityIds];
      this.metricConfigSearch = '';
      this.showMetricConfigModal = true;
    },

    isMetricSelected(metricId) {
      return this.pendingEntityMetricIds.includes(metricId);
    },

    toggleMetric(metricId) {
      const idx = this.pendingEntityMetricIds.indexOf(metricId);
      if (idx >= 0) {
        this.pendingEntityMetricIds.splice(idx, 1);
      } else {
        this.pendingEntityMetricIds.push(metricId);
      }
    },

    async saveMetricConfig() {
      this.metricConfigSaving = true;
      try {
        const displayConfig = { ...(this.entity.display_config || {}) };
        if (this.pendingEntityMetricIds.length === 0) {
          delete displayConfig.scorecard_metric_ids;
        } else {
          displayConfig.scorecard_metric_ids = [...this.pendingEntityMetricIds];
        }
        await axios.patch(`/api/entities/${this.entity.id}/display-config/`, {
          display_config: displayConfig
        });
        this.entity.display_config = displayConfig;
        this.showMetricConfigModal = false;
        this.refreshCurrentTab();
      } catch (e) {
        console.error('Failed to save metric config:', e);
      } finally {
        this.metricConfigSaving = false;
      }
    },

    clearEntityMetricOverride() {
      this.pendingEntityMetricIds = [];
    },

    startEditingName() {
      this.showNameEditModal = true;
      this.editingName = this.entity.name;

      this.$nextTick(() => {
        if (this.$refs.nameInput) {
          this.$refs.nameInput.focus();
          this.$refs.nameInput.select();
        }
      });
    },

    saveEntityName() {
      if (!this.editingName.trim() || this.editingName === this.entity.name) {
        this.cancelEditingName();
        return;
      }
      this.showNameChangeConfirm = true;
    },

    async confirmNameChange() {
      this.showNameChangeConfirm = false;

      try {
        const response = await axios.patch(`/api/entities/${this.entity.id}/`, {
          name: this.editingName.trim()
        });

        this.entity.name = response.data.name;
        this.showNameEditModal = false;
      } catch (error) {
        console.error('Error updating entity name:', error);
        alert('Failed to update entity name. Please try again.');
        this.editingName = this.entity.name;
      }
    },

    cancelEditingName() {
      this.showNameEditModal = false;
      this.editingName = this.entity.name;
      this.showNameChangeConfirm = false;
    },

    async confirmDeleteEntity() {
      this.showDeleteConfirm = false;

      try {
        const response = await axios.delete(`/api/entities/${this.entity.id}/delete-with-related/`);

        if (response.data.success) {
          // Show success message with stats
          const stats = response.data.stats;
          const statsMessage = [
            `Entity "${stats.entity_name}" deleted successfully!`,
            stats.actions_deleted > 0 ? `${stats.actions_deleted} actions` : null,
            stats.problems_deleted > 0 ? `${stats.problems_deleted} problems` : null,
            stats.threads_deleted > 0 ? `${stats.threads_deleted} threads` : null,
            stats.performance_blockers_deleted > 0 ? `${stats.performance_blockers_deleted} performance blockers` : null,
            stats.info_tag_relationships_deleted > 0 ? `${stats.info_tag_relationships_deleted} tag relationships` : null,
            stats.parent_relationships_removed > 0 ? `${stats.parent_relationships_removed} parent relationships` : null,
            stats.child_relationships_removed > 0 ? `${stats.child_relationships_removed} child relationships` : null
          ].filter(Boolean).join('\n- ');

          alert(statsMessage);

          // Redirect to entities list or home page
          this.$router.push({ name: 'Home' });
        }
      } catch (error) {
        console.error('Error deleting entity:', error);
        const errorMessage = error.response?.data?.error || 'Failed to delete entity. Please try again.';
        alert(`Error: ${errorMessage}`);
      }
    },

    async fetchAllComposableMetricIds() {
      try {
        const res = await axios.get('/api/performancedomains/');
        const ids = [];
        const keyIds = [];
        const names = {};
        for (const domain of res.data) {
          for (const cm of (domain.composable_metrics || [])) {
            if (cm.metric_id && !ids.includes(cm.metric_id)) {
              ids.push(cm.metric_id);
            }
            if (cm.primary_metric && cm.metric_id && !keyIds.includes(cm.metric_id)) {
              keyIds.push(cm.metric_id);
            }
            if (cm.metric_name) {
              names[cm.metric_id] = cm.metric_name;
            }
          }
        }
        this.composableScorecardAllMetricIds = ids;
        this.composableScorecardKeyMetricIds = keyIds.length > 0 ? keyIds : ids.slice(0, 4);
        this.composableScorecardMetricNames = names;
      } catch (e) {
        console.error('Failed to load composable metric IDs:', e);
      }
    },

    async fetchEntityDetails() {
      try {
        const response = await axios.get(`/api/entities/${this.entity_id}/`);
        this.entity = response.data;

        if (this.entity.entity_type) {
          const typeResponse = await axios.get(`/api/entity-types/${this.entity.entity_type}/`);
          this.entityType = typeResponse.data;

          this.applyDefaultObservationYears();

          const attrResponse = await axios.get(`/api/entity-attributes/`, {
            params: { entity_type_id: this.entity.entity_type }
          });
          this.entityAttributes = attrResponse.data;
        }

        const requestedTab = this.tab && this.resolveTab(this.tab);
        if (requestedTab) {
          this.selectedTab = requestedTab;
        } else {
          const savedTab = localStorage.getItem(`entity_${this.entity_id}_tab`);
          if (savedTab && this.availableTabs.includes(savedTab)) {
            this.selectedTab = savedTab;
          } else if (this.availableTabs.length > 0) {
            this.selectedTab = this.availableTabs[0];
          }
          this.$router.replace(`/entity/${this.entity_id}/${this.selectedTab}`);
        }

        this.loadedTabs.add(this.selectedTab);
      } catch (error) {
        console.error('Error fetching entity details:', error);
        if (error.response?.status === 404) {
          this.$router.push({ name: 'NotFound' });
        }
      }
    },

    applyDefaultObservationYears() {
      const tabs = this.entityType?.display_config?.layout_config?.tabs || {};
      const scorecardComponents = new Set(['key_metrics_detailed', 'composable_key_metrics']);
      const listComponents = new Set(['patient_worklist', 'ppi_module', 'quality_scorecard']);
      for (const tabComponents of Object.values(tabs)) {
        for (const comp of tabComponents) {
          const year = comp.settings?.default_observation_year;
          if (!Number.isInteger(year)) continue;
          if (scorecardComponents.has(comp.component)) {
            this.scorecard_observation_year = year;
          } else if (listComponents.has(comp.component)) {
            this.list_observation_year = year;
          }
        }
      }
    },

    refreshCurrentTab() {
      if (this.selectedTab in this.tabRefreshKeys) {
        this.tabRefreshKeys[this.selectedTab]++;
      } else {
        this.tabRefreshKeys[this.selectedTab] = 1;
      }
      this.tabRefreshKeys = { ...this.tabRefreshKeys };
    },

    getAllAttributesWithValues() {
      if (!this.entityType) return {};

      const allAttributes = {};

      if (this.entityAttributes && this.entityAttributes.length > 0) {
        this.entityAttributes.forEach(attr => {
          const group = attr.display_group || 'General';
          if (!allAttributes[group]) {
            allAttributes[group] = [];
          }

          const existingValue = this.findAttributeValue(attr.name);

          allAttributes[group].push({
            name: attr.name,
            display_name: attr.display_name,
            field_type: attr.field_type,
            value: existingValue?.value || null,
            data_source: existingValue?.data_source || null,
            confidence: existingValue?.confidence || 0,
            updated: existingValue?.updated || null,
            reason: existingValue?.reason || null,
            is_required: attr.is_required,
            description: attr.description,
            display_order: attr.display_order
          });
        });
      } else if (this.entity.attributes) {
        return this.entity.attributes;
      }

      Object.keys(allAttributes).forEach(group => {
        allAttributes[group].sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999));
      });

      return allAttributes;
    },

    findAttributeValue(attributeName) {
      if (!this.entity.attributes) return null;

      for (const group of Object.values(this.entity.attributes)) {
        const attr = group.find(a => a.name === attributeName);
        if (attr) return attr;
      }
      return null;
    },

    handleAttributeUpdate(data) {
      const allAttrs = this.getAllAttributesWithValues();
      Object.keys(allAttrs).forEach(group => {
        const attr = allAttrs[group].find(a => a.name === data.field);
        if (attr) {
          attr.value = data.value;
          attr.data_source = data.data_source;
          attr.updated = data.updated;
          attr.confidence = data.confidence;
          attr.reason = data.reason;
        }
      });
      if (this.entity.attributes) {
        Object.keys(this.entity.attributes).forEach(group => {
          const attr = this.entity.attributes[group].find(a => a.name === data.field);
          if (attr) {
            attr.value = data.value;
            attr.data_source = data.data_source;
            attr.updated = data.updated;
            attr.confidence = data.confidence;
            attr.reason = data.reason;
          }
        });
      }
    },

    // Single source of truth for component-level visibility gates. Used both to blank
    // tab content (renderComponent) and to drop a tab from the nav (availableTabs) when
    // every component in it is hidden for the current user.
    componentHidden(componentName) {
      switch (componentName) {
        case 'quality_provider_scorecard':
          // Gated by a dedicated tab-level feature (not the broad Quality
          // module nav gate). For Diverge (t5) this is granted only to
          // Central Practice Operations.
          return !canAccessFeature(FEATURES.MODULE_QUALITY_ENTITY_TAB);
        default:
          return false;
      }
    },

    resolveTab(requested) {
      const byName = this.availableTabs.find(t =>
        t === requested || t.toLowerCase().replace(/\s+/g, '-') === requested
      );
      if (byName) return byName;
      const componentName = requested.replace(/-/g, '_');
      return this.availableTabs.find(t =>
        this.getComponentsForTab(t).some(c => c.component === componentName)
      );
    },
    getComponentsForTab(tab) {
      if (!this.entityType?.display_config?.layout_config?.tabs) return [];
      const tabComponents = this.entityType.display_config.layout_config.tabs[tab] || [];
      return tabComponents.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    },

    // Per-component `settings` object from this tenant's layout_config, found by
    // component name across all tabs. Empty object when unconfigured (the default
    // for every tenant that doesn't customize the component).
    getComponentConfig(componentName) {
      const tabs = this.entityType?.display_config?.layout_config?.tabs || {};
      for (const comps of Object.values(tabs)) {
        const entry = (comps || []).find(c => c.component === componentName);
        if (entry) return entry.settings || {};
      }
      return {};
    },

    getComponentType(componentName) {
      const self = this;
      return {
        name: `${componentName}-wrapper`,
        props: ['entity', 'entityType', 'entityData', 'refreshKey', 'observationYear', 'componentConfig'],
        render() {
          if (self.loadedTabs.has(self.selectedTab)) {
            return self.renderComponent(componentName, this);
          }
          return h('div', { class: 'flex justify-center py-8' }, [
            h(FontAwesomeIcon, { icon: ['fas', 'spinner'], spin: true, class: 'text-gray-400 text-2xl' })
          ]);
        }
      };
    },

    getComponentProps(componentConfig) {
      return {
        entity: this.entity,
        entityType: this.entityType,
        entityData: this.entityData,
        refreshKey: this.currentTabRefreshKey,
        observationYear: this.observationYear,
        componentConfig: componentConfig
      };
    },

    renderComponent(componentName, componentInstance) {
      const wrapperClass = 'bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden';
      const contentClass = 'p-4';

      const componentDefinitions = {
        basic_info: {
          title: 'Information',
          renderContent: () => {
            const allAttributes = this.getAllAttributesWithValues();
            if (!allAttributes || Object.keys(allAttributes).length === 0) return null;

            return h('div', { class: 'space-y-6' },
              Object.entries(allAttributes).map(([groupName, attributes]) =>
                h('div', { key: groupName }, [
                  groupName !== 'General' && h('div', { class: 'mb-3' }, [
                    h('h3', {
                      class: 'text-sm font-semibold text-gray-700 uppercase tracking-wider'
                    }, groupName),
                    h('div', { class: 'mt-1' })
                  ]),
                  h('dl', { class: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3' },
                    attributes.map(attr =>
                      h('div', {
                        key: attr.name,
                        class: 'rounded-lg p-2 hover:bg-gray-50 transition-colors'
                      }, [
                        h(AttributeField, {
                          entityId: this.entity.id,
                          attribute: attr,
                          editable: this.canEditEntityFields,
                          showConfidence: true,
                          onUpdated: this.handleAttributeUpdate
                        })
                      ])
                    )
                  )
                ])
              )
            );
          }
        },
        action_table: {
          title: 'Action Items',
          headerAction: () => h('div', { class: 'flex items-center gap-4' }, [
            h(Generic_Form, {
              model: 'action',
              object: {
                entity: this.entity.id,
              },
              mode: 'Create',
              onUpdate: () => this.refreshCurrentTab()
            }),
            h('label', { class: 'flex items-center gap-2 text-sm text-gray-600 select-none' }, [
              h('input', {
                type: 'checkbox',
                checked: this.showCompleted,
                onChange: (e) => { this.showCompleted = e.target.checked; },
                class: 'rounded border-gray-300 text-blue-600 focus:ring-blue-dark h-4 w-4'
              }),
              h('span', {}, 'Show Completed')
            ])
          ]),
          renderContent: () => h(Action_Table, {
            entityId: this.entity.id,
            includeDescendants: true,
            completedDateIsNull: !this.showCompleted,
            class: 'max-h-[400px] overflow-auto rounded-lg'
          })
        },
        data_insights: {
          title: 'Performance Alerts',
          renderContent: () => h(ComposableAlertTable, {
            key: this.currentTabRefreshKey,
            entityId: this.entity.id,
          })
        },
        engagement_table: {
          title: 'Recent Meetings and Engagements',
          headerAction: () => h(Create_Engagement_Button, {
            entity_id: this.entity.id
          }),
          renderContent: () => h(Engagement_Table, {
            key: this.currentTabRefreshKey,
            entityId: this.entity.id,
            includeDescendants: true,
            class: 'max-h-[400px] overflow-auto rounded-lg'
          })
        },
        entity_detail_table: {
          title: 'Entity Details',
          headerAction: () => {
            // Get settings from component config
            const settings = componentInstance.componentConfig?.settings || {};
            const childEntityTypeId = settings.child_entity_type_id;

            if (!childEntityTypeId) return null;

            return h('button', {
              class: 'btn-primary-sm',
              onClick: () => {
                this.entityFormTypeId = childEntityTypeId;
                this.showEntityForm = true;
              }
            }, `Add ${settings.entity_type_name || 'Entity'}`);
          },
          renderContent: () => {
            // Get configuration from component settings
            const settings = componentInstance.componentConfig?.settings || {};
            const childEntityTypeId = settings.child_entity_type_id;

            if (!childEntityTypeId) {
              return h('div', { class: 'p-4 text-sm text-gray-500' },
                'Entity type not configured for this table'
              );
            }

            return h(Entity_Detail_Table, {
              key: this.currentTabRefreshKey,
              entityTypeId: childEntityTypeId,
              rootEntityId: this.entity.id,
              includeDescendants: true,
              autoHeight: settings.auto_height || false,
              class: 'max-h-[400px] overflow-auto rounded-lg'
            });
          }
        },
        // Keep staff_table for backward compatibility
        staff_table: {
          title: 'Staff Members',
          headerAction: this.canEditHierarchy ? () => h('button', {
            class: 'btn-primary',
            onClick: () => {
              this.entityFormTypeId = 9; // Staff entity type ID
              this.showEntityForm = true;
            },
          }, [
            h(FontAwesomeIcon, { icon: ['fas', 'plus'], class: 'text-white' }),
            'Create'
          ]): null,
          renderContent: () => h(Entity_Detail_Table, {
            key: this.currentTabRefreshKey,
            entityTypeId: 9, // Hardcoded Staff entity type ID
            rootEntityId: this.entity.id,
            includeDescendants: true,
            class: 'max-h-[400px] overflow-auto rounded-lg'
          })
        },
        threads: {
          title: 'Engagement Threads',
          renderContent: () => h(EngagementTrackerPanel, {
            key: this.currentTabRefreshKey,
            entityId: this.entity.id
          })
        },
        surveys: {
          title: 'Surveys',
          renderContent: () => h(Survey_Table, {
            key: this.currentTabRefreshKey,
            entityId: this.entity.id,
            autoHeight: true,
            class: 'rounded-lg'
          })
        },
        key_metrics_detailed: {
          title: 'Key Metrics - Detailed',
          customHeader: true,
          headerAction: () => {
            return h('div', { class: 'flex items-center gap-2' }, [
              h('span', { class: 'text-sm text-gray-600' }, 'Performance Year'),
              h('select', {
                class: 'text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent',
                value: this.scorecard_observation_year,
                onChange: (e) => {
                  this.scorecard_observation_year = parseInt(e.target.value);
                }
              }, [
                h('option', { value: 2024 }, '2024'),
                h('option', { value: 2025 }, '2025'),
                h('option', { value: 2026 }, '2026')
              ])
            ]);
          },
          renderContent: () => {
            // Some entity types are not valid grains — a contract dimension
            // (Payer, Contract Pool, Contract (Cohort)), or a type with no
            // providers beneath it. The studio engine returns zero rows for
            // either, so the card would render empty. Fall back to Practice,
            // which still scopes to this entity's subtree via entity_id.
            // No grain plumbing needed: the scorecard aggregates by entity SCOPE
            // (entity_type 'Tenant' + entity_id), which is uniform across every
            // entity type — including contract dimensions like Contract Pool that
            // cannot be a rollup grain at all. See the component for detail.
            return h(Practice_Metric_Scorecard_Detailed, {
              entity: componentInstance.entity,
              observationYear: this.scorecard_observation_year,
              key: `${this.currentTabRefreshKey}-${this.scorecard_observation_year}-metric-detailed`,
              class: 'rounded-lg',
              autoHeight: true,
              contentTypeModel: 'entity'
            });
          }
        },
        composable_key_metrics: {
          title: 'Key Metrics',
          customHeader: true,
          headerAction: () => {
            const hasEntityOverride = this.getMetricConfigSource() === 'entity';
            return h('div', { class: 'flex items-center gap-4' }, [
              h('div', { class: 'flex items-center gap-2' }, [
                h('span', { class: 'text-sm text-gray-600' }, 'Performance Year'),
                h('select', {
                  class: 'text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent',
                  value: this.scorecard_observation_year,
                  onChange: (e) => {
                    this.scorecard_observation_year = parseInt(e.target.value);
                  }
                }, [
                  h('option', { value: 2024 }, '2024'),
                  h('option', { value: 2025 }, '2025'),
                  h('option', { value: 2026 }, '2026')
                ])
              ]),
              h(Toggle, {
                name: 'Pivot',
                modelValue: this.composablePivotMode,
                'onUpdate:modelValue': (val) => { this.composablePivotMode = val; }
              }),
              this.canEditEntityFields && h('button', {
                class: 'relative p-1.5 rounded hover:bg-gray-100 transition-colors',
                title: hasEntityOverride ? 'Metrics customized for this entity' : 'Configure metrics',
                onClick: (e) => { e.stopPropagation(); this.openMetricConfig(); }
              }, [
                h(FontAwesomeIcon, {
                  icon: ['fas', 'gear'],
                  class: ['w-4 h-4', hasEntityOverride ? 'text-blue-dark' : 'text-gray-400']
                }),
                hasEntityOverride && h('span', {
                  class: 'absolute -top-0.5 -right-0.5 w-2 h-2 bg-blue-dark rounded-full'
                })
              ])
            ]);
          },
          renderContent: () => h(Composable_Metric_Scorecard, {
            entity: componentInstance.entity,
            entityTypeName: componentInstance.entityType?.type_name || 'Practice',
            observationYear: this.scorecard_observation_year,
            key: `${this.currentTabRefreshKey}-${this.scorecard_observation_year}-${this.composablePivotMode}-composable`,
            autoHeight: true,
            viewMode: this.composablePivotMode ? 'pivot' : 'scorecard',
            metricIds: this.getResolvedMetricIds(componentInstance),
            allMetricIds: this.composableScorecardAllMetricIds,
            metricNames: this.composableScorecardMetricNames
          })
        },
        attribution: {
          title: 'Contract Attribution',
          renderContent: () => h(Contract_Attribution_Table, {
            key: this.currentTabRefreshKey,
            entity_id: this.toInteger(this.entity.id),
            addContractDetails: true,
            class: 'rounded-lg shadow-sm'
          })
        },
        patient_worklist: {
          title: 'Patient Performance Index',
          renderContent: () => h(PatientWorklist, {
            key: this.currentTabRefreshKey,
            worklistIdProp: 1,
            entityId: this.toInteger(this.entity.id),
            observationYear: this.list_observation_year,
            showColumnBar: false,
            class: 'rounded-lg'
          })
        },
        ppi_module: {
          title: 'PPI',
          renderContent: () => h(PPIModule, {
            key: this.currentTabRefreshKey,
            entityId: this.toInteger(this.entity.id),
            observationYear: this.list_observation_year,
          })
        },
        entity_context: {
          title: 'Recent Entity Context',
          renderContent: () => h(Details_EntityContext, {
            key: this.currentTabRefreshKey,
            entityId: this.entity.id,
            entityType: this.entity.entity_type
          })
        },
        adt_alerts: {
          title: 'Recent ADT Alerts',
          customHeader: true,
          headerAction: () => {
            return h('div', { class: 'flex items-center gap-3' }, [
              h('div', { class: 'flex items-center gap-2 text-sm text-gray-600' }, [
                h('span', {}, 'Start'),
                h('input', {
                  type: 'date',
                  value: this.adtEpisodeStart,
                  class: 'text-sm border border-gray-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent',
                  onInput: (e) => {
                    this.adtEpisodeStart = e.target.value;
                  }
                })
              ]),
              h('div', { class: 'flex items-center gap-2 text-sm text-gray-600' }, [
                h('span', {}, 'End'),
                h('input', {
                  type: 'date',
                  value: this.adtEpisodeEnd,
                  class: 'text-sm border border-gray-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent',
                  onInput: (e) => {
                    this.adtEpisodeEnd = e.target.value;
                  }
                })
              ]),
              h('button', {
                class: 'inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md bg-white hover:bg-gray-50',
                onClick: () => this.resetAdtEpisodeRange(),
                title: 'Reset to last 30 days'
              }, 'Last 30 days'),
              h('label', { class: 'flex items-center gap-2 text-sm text-gray-600' }, [
                h('input', {
                  type: 'checkbox',
                  checked: this.showActiveTCM,
                  class: 'rounded border-gray-300 text-blue-600 focus:ring-blue-dark h-4 w-4',
                  onChange: (e) => {
                    this.showActiveTCM = e.target.checked;
                  }
                }),
                h('span', {}, 'Show Active TCM')
              ])
            ]);
          },
          renderContent: () => {
            return h(ADT_Episodes_List, {
              entityId: this.toInteger(this.entity_id),
              autoHeight: false,
              showActiveTCM: this.showActiveTCM,
              sqlFilters: this.adtEpisodesSqlFilters,
              key: this.adtEpisodesKey
            });
          }
        },
        toc_sankey: {
          title: 'Transitions of Care',
          renderContent: () => h(TOCSankeyInsight, {
            key: this.currentTabRefreshKey,
            metricId: 51,
            entityId: this.toInteger(this.entity_id)
          })
        },
        entity_table: {
          title: 'Entity Hierarchy',
          renderContent: () => h(Entity_Table, {
            key: this.currentTabRefreshKey,
            rootEntityId: this.toInteger(this.entity.id),
            entityTypeId: this.entity.entity_type,
            autoHeight: false,
            class: 'rounded-lg'
          })
        },
        quality_scorecard: {
          title: 'Quality Patient Worklist',
          renderContent: () => {
            const settings = componentInstance.componentConfig?.settings || {};
            return h(PatientWorklist, {
              worklistIdProp: settings.worklist_id || 45,
              entityId: this.toInteger(this.entity.id),
              observationYear: this.list_observation_year,
              key: `${this.currentTabRefreshKey}-quality-worklist`,
            });
          }
        },
        care_gap_summary: {
          title: 'Care Gap Summary',
          hidden: () => this.componentHidden('quality_provider_scorecard'),
          renderContent: () => h(CareGapSummary, {
            entity: this.entity,
            refreshKey: this.currentTabRefreshKey,
            key: `${this.currentTabRefreshKey}-care-gap-summary`,
          }),
        },
        quality_provider_scorecard: {
          title: 'Quality Scorecard',
          hidden: () => this.componentHidden('quality_provider_scorecard'),
          renderContent: () => h(Quality_Scorecard, {
            entity: this.entity,
            entityType: this.entityType,
            key: `${this.currentTabRefreshKey}-quality-scorecard`,
          }),
        },
        attribution_health: {
          title: 'Attribution Health',
          renderContent: () => h(Attribution_Health, {
            key: this.currentTabRefreshKey,
            entityId: this.toInteger(this.entity.id)
          })
        },
        requirement_table: {
          title: 'Notes',
          renderContent: () => {
            return h(EntityNotes, {
              key: this.currentTabRefreshKey,
              entityId: this.toInteger(this.entity.id),
              includeDescendants: true,
            });
          }
        },
        intelligence: {
          title: 'Pre-Meeting Brief',
          noWrapper: true,
          hidden: () => this.componentHidden('intelligence'),
          renderContent: () => h(EntityIntelligencePanel, {
            entityId: this.toInteger(this.entity.id),
            entityName: this.entity?.name || '',
          }),
        },
        strategic_assessments: {
          title: 'Strategic Assessments',
          renderContent: () => h(StrategicAssessments, {
            key: this.currentTabRefreshKey,
            entityId: this.toInteger(this.entity.id)
          })
        },
        actuarial_benchmarks: {
          title: 'Actuarial benchmarks',
          noWrapper: true,
          renderContent: () => h(ActuarialBenchmarks, {
            key: this.currentTabRefreshKey,
            entityId: this.toInteger(this.entity.id)
          })
        },
        entity_reports: {
          title: 'Reports',
          noWrapper: true,
          renderContent: () => h(EntityReports, {
            key: this.currentTabRefreshKey,
            entityId: this.toInteger(this.entity.id),
            componentConfig: this.getComponentConfig('entity_reports')
          })
        },
        engagement_tracker: {
          title: 'Engagement Tracker',
          noWrapper: true,
          hidden: () => this.componentHidden('engagement_tracker'),
          renderContent: () => h(EngagementTrackerPanel, {
            key: this.currentTabRefreshKey,
            entityId: this.toInteger(this.entity.id),
            componentConfig: this.getComponentConfig('engagement_tracker')
          })
        }
      };

      const config = componentDefinitions[componentName];
      if (!config) return null;
      if (config.hidden && config.hidden()) return null;

      if (componentInstance.componentConfig?.settings?.title) {
        config.title = componentInstance.componentConfig.settings.title;
      }

      const headerSlots = {};
      if (config.headerAction) {
        headerSlots.default = () => config.headerAction();
      }

      if (config.noWrapper) {
        return config.renderContent();
      }

      const headerProps = { title: config.title };
      if (config.subtitle) headerProps.subtitle = config.subtitle;

      return h('div', { class: wrapperClass }, [
        h(ComponentHeader, headerProps, headerSlots),
        h('div', { class: contentClass }, [config.renderContent()])
      ]);
    },

    selectTab(tab) {
      this.selectedTab = tab;
      localStorage.setItem(`entity_${this.entity_id}_tab`, tab);
      const targetPath = `/entity/${this.entity_id}/${tab}`;
      if (this.$route.path !== targetPath) {
        this.$router.replace(targetPath);
      }
    },

    dateToIsoStart(dateStr) {
      return calendarDate(dateStr).toISOString();
    },

    dateToIsoEnd(dateStr) {
      const d = calendarDate(dateStr);
      d.setHours(23, 59, 59, 999);
      return d.toISOString();
    },

    resetAdtEpisodeRange() {
      this.adtEpisodeStart = new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().slice(0, 10);
      this.adtEpisodeEnd = new Date().toISOString().slice(0, 10);
    }
  }
};
</script>
